import React, { useState } from "react";
import moment from "moment";
import { Card, Typography, Pagination, Table, Input, Button, Popconfirm, message, Segmented } from "antd";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { capitalizeWords } from "../../../../components/icons/ApplicationColor";
import { DeleteOutlined } from "@ant-design/icons";
import useCommonListing from "../../../../components/AdminComponents/customHooks/useCommonListing";
import axiosInstance from "../../../../api/Axios";
import CommonTopDetails from "../../../../components/AdminComponents/CommonTopDetails";
import AddLibraryModal from "./AddLibraryModal";

const { Title } = Typography;

const Library = (props) => {
    const userInfo = useSelector((state) => state.user);
    const { id } = useParams();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [loadAgain, setLoadAgain] = useState(false);
    const { data1, canEdit } = props;

    const [filterOptions, setFilterOptions] = useState({
        courseTypes: [
            { course_type: "Course", count: 0 },
            { course_type: "Bundle", count: 0 },
            { course_type: "Learning Path", count: 0 },
        ],
    });
    const [selectedCourseType, setSelectedCourseType] = useState("Course");

    const LoadAPI = () => {
        setLoadAgain((prev) => !prev);
    };

    const {
        data,
        itemsLength,
        loading,
        filterData,
        onShowSizeChange,
        onPageChange,
        handleSearchChange,
        setFilterUrl,
    } = useCommonListing({
        passSize: 10,
        passPage: 1,
        passURL: `/customer/courselibrary/?organization=${id}&course_type=Course`,
        includeStatus: true,
        loadAgain: loadAgain,
    });

    React.useEffect(() => {
        const fetchCounts = async () => {
            try {
                const types = ["Course", "Bundle", "Learningpath"];
                const countPromises = types.map(type =>
                    axiosInstance.get(`/customer/courselibrary/?organization=${id}&status=True&course_type=${type}&page_size=1`)
                );
                const results = await Promise.all(countPromises);
                const counts = results.map((res, index) => ({
                    course_type: types[index] === "Learningpath" ? "Learning Path" : types[index],
                    count: res.data?.count || 0,
                    api_type: types[index]
                }));
                setFilterOptions({ courseTypes: counts });
            } catch (err) {
                console.error('Error fetching course type counts', err);
            }
        };
        fetchCounts();
    }, [id, loadAgain]);

    React.useEffect(() => {
        // Since the base passURL already has course_type=Course,
        // we only need to set filter_url when it's NOT Course.
        const newFilterUrl = selectedCourseType === "Course"
            ? ""
            : `course_type=${selectedCourseType === "Learning Path" ? "Learningpath" : selectedCourseType}`;

        if (filterData.filter_url !== newFilterUrl) {
            setFilterUrl(newFilterUrl);
        }
    }, [selectedCourseType, setFilterUrl, filterData.filter_url]);

    const { pageSize, currentPage } = filterData;
    const processedData = React.useMemo(() => {
        const processItems = (items, parentId = "") => {
            return items?.map((item) => {
                const uniqueKey = parentId ? `${parentId}-${item.id}` : `${item.id}`;
                const newItem = { ...item, tableUniqueKey: uniqueKey };
                if (newItem.children && newItem.children.length > 0) {
                    newItem.children = processItems(newItem.children, uniqueKey);
                } else if (newItem.children) {
                    // Ensure empty children arrays are removed to prevent expand icons
                    delete newItem.children;
                }
                return newItem;
            });
        };
        return processItems(data);
    }, [data]);

    function getCookie(name) {
        const cookies = document.cookie.split("; ");
        for (let i = 0; i < cookies.length; i++) {
            const [cookieName, cookieValue] = cookies[i].split("=");
            if (cookieName === name) {
                return cookieValue;
            }
        }
        return null;
    }
    const csrfToken = getCookie("csrftoken");

    const handleDelete = async (record) => {
        try {
            await axiosInstance.delete(`/customer/courselibrarydelete/`, {
                params: {
                    course_id: record.id,
                    customer_id: id,
                },
                headers: {
                    "X-CSRFToken": csrfToken,
                },
            });
            message.success("Course deleted successfully from library");
            LoadAPI();
            if (props.LoadUser) {
                props.LoadUser();
            }
        } catch (err) {
            console.log(err);
            message.error("Failed to delete course from library");
        }
    };

    const columns = [
        {
            title: "Course ID",
            dataIndex: "course_id",
            key: "course_id",
            width: 150,
            fixed: "left",
        },
        {
            title: "Course Name",
            dataIndex: "name",
            key: "name",
            width: 200,
            ellipsis: true,
            fixed: "left",
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            width: 150,
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            width: 100,
        },
        {
            title: "Mode",
            dataIndex: "mode",
            key: "mode",
            width: 120,
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 150,
            render: (text) => (text ? moment(text).format("MMM-DD-YYYY") : "-"),
        },
        {
            title: capitalizeWords("actions"),
            key: "actions",
            align: "center",
            width: 100,
            fixed: "right",
            onCell: (record) => ({
                onClick: (e) => {
                    e.stopPropagation();
                    if (canEdit === false) {
                        // Additional safety for click handling
                    }
                },
            }),
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure you want to delete this course?"
                    onConfirm={() => canEdit !== false && handleDelete(record)}
                    okText="Yes"
                    cancelText="No"
                    disabled={canEdit === false}
                    placement="leftTop"
                >
                    <DeleteOutlined
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            cursor: canEdit === false ? "not-allowed" : "pointer",
                            fontSize: "1.3rem",
                            color: canEdit === false ? "#d9d9d9" : "#ff4d4f",
                            opacity: canEdit === false ? 0.5 : 1,
                        }}
                    />
                </Popconfirm>
            ),
        },
    ];

    const FilterSlider = () => {
        const order = ["Course", "Bundle", "Learning Path"];
        const options = order.map(type => {
            const found = filterOptions.courseTypes.find(ct => ct.course_type === type);
            return {
                label: `${type} (${found ? found.count : 0})`,
                value: type,
            };
        });

        return (
            <div className="filter-slider-container">
                <style>
                    {`
                        .filter-slider-container {
                            display: flex;
                            flex-direction: column;
                            gap: 10px;
                        }
                        .filter-slider-container .ant-segmented {
                            background: #D9D9D9;
                            border-radius: 10px;
                            padding: 4px;
                            width: fit-content;
                        }
                        .filter-slider-container .ant-segmented-item {
                            border-radius: 8px;
                            transition: all 0.3s;
                        }
                        .filter-slider-container .ant-segmented-item-selected {
                            background: #254061 !important;
                            color: #fff !important;
                        }
                        .filter-slider-container .ant-segmented-item:not(.ant-segmented-item-selected) {
                            color: #3F3F3F;
                        }
                        .filter-slider-container .ant-segmented-item-label {
                            padding: 4px 16px;
                            font-weight: 500;
                            font-size: 14px;
                        }
                    `}
                </style>
                <Title level={5} style={{ margin: 0, paddingLeft: "5px" }}>
                    Library
                </Title>
                <Segmented
                    options={options}
                    value={selectedCourseType}
                    onChange={setSelectedCourseType}
                />
            </div>
        );
    };

    return (
        <Card style={{ width: "100%", marginBottom: "10px" }} hoverable>
            <CommonTopDetails
                leftContent={<FilterSlider />}
                title="Course Library"
                titleSuffix={`${itemsLength || 0} item(s)`}
                searchPlaceholder="Search..."
                searchAction={handleSearchChange}
                showAddButton={true}
                addButtonProps={{
                    title: "Add Course",
                    modalType: "PopUP",
                    modalProps: () => setIsAddModalOpen(true),
                    disabled: canEdit === false,
                }}
                filterAction={false}
                marginPass="15px"
                paddingInlinepass="10px"
            />
            <Table
                columns={columns}
                dataSource={processedData}
                rowKey="tableUniqueKey"
                size="small"
                loading={loading}
                className="table-container"
                style={{ margin: "1rem" }}
                pagination={false}
                scroll={{
                    x: 1000,
                }}
                bordered
                expandable={{
                    rowExpandable: (record) =>
                        record.children && record.children.length > 0,
                }}
            />
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "1rem",
                }}
            >
                <Pagination
                    size="small"
                    showSizeChanger
                    onShowSizeChange={onShowSizeChange}
                    onChange={onPageChange}
                    pageSizeOptions={["5", "10", "15", "20", "50"]}
                    current={currentPage}
                    defaultPageSize={pageSize}
                    total={itemsLength}
                />
            </div>
            {isAddModalOpen && (
                <AddLibraryModal
                    open={isAddModalOpen}
                    onCancel={() => setIsAddModalOpen(false)}
                    onSuccess={() => {
                        LoadAPI();
                        if (props.LoadUser) {
                            props.LoadUser();
                        }
                    }}
                    customerId={id}
                    parentKeyId={userInfo?.data?.organization?.id}
                />
            )}
        </Card>
    );
};

export default Library;
